import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import { createUserWithEmailAndPassword } from "firebase/auth";
import { auth } from "./firebase";
import logo from "./assets/image/logo_web.png";
import Banner from './components/Banner';
import ProgressStepper from './components/ProgressStepper';

export default function Register({ onRegister }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");

  async function handleRegister(e) {
    e.preventDefault();
    setError("");
    try {
      const userCred = await createUserWithEmailAndPassword(
        auth,
        email,
        password
      );
      // Call onRegister to update parent state
      if (onRegister) {
        onRegister(userCred.user);
      }
    } catch (err) {
      // User-friendly error messages instead of raw Firebase errors
      switch (err.code) {
        case 'auth/email-already-in-use':
          setError(t('auth.errorEmailInUse'));
          break;
        case 'auth/weak-password':
          setError(t('auth.errorWeakPassword'));
          break;
        case 'auth/invalid-email':
          setError(t('auth.errorInvalidEmailFormat'));
          break;
        case 'auth/too-many-requests':
          setError(t('auth.errorTooManyRequests'));
          break;
        case 'auth/network-request-failed':
          setError(t('auth.errorNetworkFailed'));
          break;
        default:
          setError(err.code ? t('auth.errorGenericRegister') : err.message);
      }
    }
  }

  function handleShowLogin() {
    navigate('/login');
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-950 via-gray-900 to-black text-gray-50 relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -left-32 -top-32 w-72 h-72 bg-red-600/30 blur-3xl rounded-full" />
        <div className="absolute right-0 top-10 w-96 h-96 bg-rose-500/20 blur-3xl rounded-full" />
        <div className="absolute -right-24 bottom-0 w-80 h-80 bg-amber-400/10 blur-3xl rounded-full" />
      </div>

      <div className="relative z-10 max-w-6xl mx-auto px-6 py-10 lg:py-14">
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => navigate('/')}
            className="inline-flex items-center gap-2 text-sm text-gray-200 hover:text-white transition"
          >
            <span className="text-lg">←</span>
            {t('common.back')}
          </button>
          <Banner className="w-48 lg:w-56" />
        </div>

        {/* Account creation progress */}
        <ProgressStepper
          steps={[
            { label: 'Create Account' },
            { label: 'Company Setup' },
            { label: 'Start Trial' },
          ]}
          currentStep={0}
        />

        <div className="grid lg:grid-cols-2 gap-8 items-center">
          <div className="hidden lg:block space-y-6">
            <div className="inline-flex items-center gap-3 px-3 py-1 rounded-full bg-white/5 border border-white/10 text-xs uppercase tracking-[0.2em] text-gray-200">
              <span className="h-2 w-2 rounded-full bg-emerald-400 animate-pulse" />
              Create your workspace
            </div>
            <h1 className="text-3xl lg:text-4xl font-bold text-white leading-tight">
              {t('auth.joinBMC')}
            </h1>
            <p className="text-gray-300 text-base leading-relaxed">
              {t('auth.registerDescription')}
            </p>
            <div className="flex items-center gap-4 text-sm text-gray-300">
              <div className="flex items-center gap-2">
                <span className="h-2 w-2 rounded-full bg-emerald-400" /> {t('auth.instantAccess')}
              </div>
              <div className="flex items-center gap-2">
                <span className="h-2 w-2 rounded-full bg-sky-400" /> {t('auth.draftAutosave')}
              </div>
              <div className="flex items-center gap-2">
                <span className="h-2 w-2 rounded-full bg-amber-300" /> {t('auth.ukHosted')}
              </div>
            </div>
          </div>

          <div className="relative">
            <div className="absolute inset-0 -m-4 bg-white/5 blur-xl rounded-3xl" />
            <div className="relative bg-gray-900/70 backdrop-blur-lg border border-white/10 rounded-3xl shadow-2xl p-8 lg:p-10">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <img src={logo} alt="BuildMyChecks Logo" className="w-12 h-12 object-contain" />
                  <div>
                    <p className="text-xs uppercase tracking-[0.2em] text-gray-400">BuildMyChecks</p>
                    <h2 className="text-xl font-semibold text-white">{t('auth.createAccount')}</h2>
                  </div>
                </div>
                <button
                  onClick={() => navigate('/login')}
                  className="text-sm text-gray-300 hover:text-white transition"
                >
                  {t('nav.signIn')}
                </button>
              </div>

              <form onSubmit={handleRegister} className="space-y-4">
                <input
                  type="email"
                  placeholder={t('auth.workEmail')}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 transition"
                />
                <input
                  type="password"
                  placeholder={t('auth.createPassword')}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 transition"
                />

                <button
                  type="submit"
                  className="w-full bg-gradient-to-r from-red-600 to-rose-600 hover:from-red-500 hover:to-rose-500 text-white py-3 rounded-xl font-semibold shadow-lg shadow-red-900/30 transition-transform transform hover:translate-y-[-1px]"
                >
                  {t('auth.createAccountBtn')}
                </button>
              </form>

              {error && (
                <p className="text-red-400 text-sm mt-3">{error}</p>
              )}

              <p className="text-gray-400 text-xs mt-6">
                {t('auth.termsAgreement')}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
